import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../Utils/Function';
import { FaPhone, FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaTrash, FaCheckCircle, FaClock, FaSpinner, FaTimesCircle, FaTools } from 'react-icons/fa';

const AdminBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusModal, setStatusModal] = useState({ show: false, booking: null, newStatus: '' });
    const [deleteModal, setDeleteModal] = useState({ show: false, booking: null });
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/booking/getBooking.php`);
            if (response.data.status === 200) {
                // Sort by booking_id descending (Latest first)
                const sorted = [...response.data.data].sort((a, b) =>
                    (parseInt(b.booking_id || b.booked_id)) - (parseInt(a.booking_id || a.booked_id))
                );
                setBookings(sorted);
            }
        } catch (error) {
            console.error('Error fetching bookings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateStatus = (booking, newStatusCode) => {
        setStatusModal({
            show: true,
            booking,
            newStatus: newStatusCode
        });
    };

    const confirmUpdateStatus = async () => {
        if (!statusModal.booking || !statusModal.newStatus) return;

        setIsUpdating(true);
        try {
            const response = await axios.put(`${BASE_URL}/booking/updateBooking.php`, {
                booking_id: statusModal.booking.booking_id || statusModal.booking.booked_id,
                status: statusModal.newStatus
            });
            if (response.data.status === 200) {
                fetchBookings();
                setStatusModal({ show: false, booking: null, newStatus: '' });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status. Check backend connection.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = (booking) => {
        setDeleteModal({
            show: true,
            booking
        });
    };

    const confirmDelete = async () => {
        if (!deleteModal.booking) return;
        const id = deleteModal.booking.booking_id || deleteModal.booking.booked_id;

        setIsDeleting(true);
        try {
            const response = await axios.delete(`${BASE_URL}/booking/deleteBooking.php?booking_id=${id}`);
            if (response.data.status === 200) {
                fetchBookings();
                setDeleteModal({ show: false, booking: null });
            }
        } catch (error) {
            console.error('Error deleting booking:', error);
            alert('Failed to delete booking. Check backend connection.');
        } finally {
            setIsDeleting(false);
        }
    };

    const filterOptions = [
        { label: 'ALL STATUS', value: 'All' },
        { label: 'PENDING', value: '1' },
        { label: 'IN PROGRESS', value: '2' },
        { label: 'COMPLETED', value: '3' },
        { label: 'CANCELLED', value: '4' }
    ];

    const filteredBookings = bookings.filter(booking => {
        // Handle filterStatus by comparing numeric code or 'All'
        const matchesStatus = filterStatus === 'All' || booking.status === filterStatus;
        const matchesSearch = booking.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.mobile_no.includes(searchTerm) ||
            booking.serv_name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    const statusConfig = {
        '1': { label: 'Pending', icon: <FaClock />, color: '#ffc107', bg: 'rgba(255, 193, 7, 0.1)' },
        '2': { label: 'On Progress', icon: <FaTools />, color: '#0dcaf0', bg: 'rgba(13, 202, 240, 0.1)' },
        '3': { label: 'Complete', icon: <FaCheckCircle />, color: '#198754', bg: 'rgba(25, 135, 84, 0.1)' },
        '4': { label: 'Cancelled', icon: <FaTimesCircle />, color: '#dc3545', bg: 'rgba(220, 53, 69, 0.1)' }
    };

    return (
        <div className="admin-bookings-page">
            <div className="d-flex justify-content-between align-items-end mb-5">
                <div>
                    <span className="section-index">REPORTS</span>
                    <h2 className="display-6 text-white text-uppercase m-0" style={{ letterSpacing: '3px' }}>Booking Management</h2>
                </div>
                <div className="booking-controls d-flex gap-3">
                    <input
                        type="text"
                        className="admin-search-input"
                        placeholder="Search by name, mobile or service..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="admin-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        {filterOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center py-5 mt-5">
                    <FaSpinner className="spinner mb-3" style={{ fontSize: '2rem', color: 'var(--secondary-color)' }} />
                    <p className="text-white-50 letter-spacing-2">RETRIEVING RECORDS...</p>
                </div>
            ) : (
                <div className="bookings-list">
                    {filteredBookings.map((booking) => (
                        <div key={booking.booking_id || booking.booked_id} className="booking-admin-card shadow-lg">
                            <div className="card-top-accent" style={{ background: statusConfig[booking.status]?.color || '#fff' }}></div>
                            <div className="row g-0">
                                <div className="col-lg-4 p-4 border-end border-white-05">
                                    <div className="user-profile-section d-flex align-items-center gap-3 mb-3">
                                        <div className="user-avatar-placeholder">
                                            {booking.user_name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h5 className="m-0 text-white">{booking.user_name}</h5>
                                        </div>
                                    </div>
                                    <div className="contact-info d-flex flex-column gap-2 mt-4">
                                        <div className="info-item">
                                            <FaPhone className="item-icon" />
                                            <span>{booking.mobile_no}</span>
                                        </div>
                                        <div className="info-item">
                                            <FaMapMarkerAlt className="item-icon" />
                                            <span className="text-truncate" title={booking.address}>{booking.address}</span>
                                        </div>
                                        <div className="info-item">
                                            <FaCalendarAlt className="item-icon" />
                                            <span>{booking.date_time || booking.booked_date}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-5 p-4 border-end border-white-05">
                                    <div className="service-details">
                                        <span className="section-index" style={{ fontSize: '0.6rem' }}>REQUESTED SERVICE</span>
                                        <h4 className="text-white mb-3">{booking.serv_name}</h4>
                                        <div className="booking-meta">
                                            <div className="status-indicator" style={{
                                                color: statusConfig[booking.status]?.color || '#fff',
                                                background: statusConfig[booking.status]?.bg || 'rgba(255,255,255,0.05)'
                                            }}>
                                                {statusConfig[booking.status]?.icon}
                                                <span className="ms-2">{(statusConfig[booking.status]?.label || 'Unknown').toUpperCase()}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-3 p-4 d-flex flex-column justify-content-center gap-3">
                                    <select
                                        className="btn-status-update"
                                        value={booking.status}
                                        onChange={(e) => handleUpdateStatus(booking, e.target.value)}
                                        style={{ color: '#fff' }}
                                    >
                                        <option value="1" style={{ background: '#0a0a0a' }}>SET TO PENDING</option>
                                        <option value="2" style={{ background: '#0a0a0a' }}>SET TO IN PROGRESS</option>
                                        <option value="3" style={{ background: '#0a0a0a' }}>SET TO COMPLETED</option>
                                        <option value="4" style={{ background: '#0a0a0a' }}>SET TO CANCELLED</option>
                                    </select>
                                    <button className="btn-delete-booking" onClick={() => handleDelete(booking)}>
                                        <FaTrash className="me-2" /> DELETE RECORD
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}

                    {filteredBookings.length === 0 && (
                        <div className="empty-results py-5 text-center">
                            <h4 className="text-white-50">NO MATCHING RECORDS FOUND</h4>
                        </div>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content confirm-modal">
                        <div className="modal-header-custom border-0 pb-0">
                            <h4 className="m-0 text-white">AUTHORIZATION REQUIRED</h4>
                            <button className="btn-close-modal" onClick={() => setDeleteModal({ show: false, booking: null })}><FaTimes /></button>
                        </div>
                        <div className="modal-body-custom pt-4 text-center">
                            <div className="mb-4">
                                <div className="confirm-icon-wrap delete-glow">
                                    <FaTrash />
                                </div>
                            </div>
                            <h5 className="text-white mb-3">Permanent Deletion</h5>
                            <p className="text-white-50 small mb-4">
                                Are you sure you want to delete the booking for <strong className="text-white">{deleteModal.booking?.user_name}</strong>?
                                This record will be permanently purged from the system.
                            </p>

                            <div className="d-flex gap-3 mt-5">
                                <button className="btn-admin-submit btn-delete-final flex-grow-1" onClick={confirmDelete} disabled={isDeleting}>
                                    {isDeleting ? 'DELETING...' : 'CONFIRM DELETION'}
                                </button>
                                <button className="btn-admin-outline px-4" onClick={() => setDeleteModal({ show: false, booking: null })} disabled={isDeleting}>
                                    CANCEL
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {statusModal.show && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content confirm-modal">
                        <div className="modal-header-custom border-0 pb-0">
                            <h4 className="m-0 text-white">AUTHORIZATION REQUIRED</h4>
                            <button className="btn-close-modal" onClick={() => setStatusModal({ show: false, booking: null, newStatus: '' })}><FaTimes /></button>
                        </div>
                        <div className="modal-body-custom pt-4 text-center">
                            <div className="mb-4">
                                <div className="confirm-icon-wrap" style={{
                                    color: statusConfig[statusModal.newStatus]?.color,
                                    background: statusConfig[statusModal.newStatus]?.bg
                                }}>
                                    {statusConfig[statusModal.newStatus]?.icon}
                                </div>
                            </div>
                            <h5 className="text-white mb-3">Confirm Status Change</h5>
                            <p className="text-white-50 small mb-4">
                                Are you sure you want to update the status of <strong>{statusModal.booking?.user_name}</strong>'s booking
                                for <strong>{statusModal.booking?.serv_name}</strong> to <span style={{ color: statusConfig[statusModal.newStatus]?.color }}>
                                    {statusConfig[statusModal.newStatus]?.label.toUpperCase()}
                                </span>?
                            </p>

                            <div className="d-flex gap-3 mt-5">
                                <button className="btn-admin-submit flex-grow-1" onClick={confirmUpdateStatus} disabled={isUpdating}>
                                    {isUpdating ? 'PROCESSING...' : 'CONFIRM UPDATE'}
                                </button>
                                <button className="btn-admin-outline px-4" onClick={() => setStatusModal({ show: false, booking: null, newStatus: '' })} disabled={isUpdating}>
                                    CANCEL
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .admin-search-input {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: white;
                    padding: 10px 20px;
                    border-radius: 2px;
                    font-size: 0.8rem;
                    width: 300px;
                    outline: none;
                }

                .admin-search-input:focus { border-color: var(--secondary-color); }

                .admin-select {
                    background: #0a0a0a;
                    border: 1px solid rgba(255,255,255,0.1);
                    color: white;
                    padding: 10px 15px;
                    font-size: 0.8rem;
                    border-radius: 2px;
                    outline: none;
                    cursor: pointer;
                }

                .booking-admin-card {
                    background: rgba(20, 20, 20, 0.4);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    margin-bottom: 30px;
                    position: relative;
                    border-radius: 4px;
                    overflow: hidden;
                    transition: all 0.3s ease;
                }

                .booking-admin-card:hover { border-color: rgba(255, 255, 255, 0.1); transform: translateX(5px); }

                .card-top-accent { height: 3px; position: absolute; top: 0; left: 0; width: 100%; }

                .border-white-05 { border-color: rgba(255,255,255,0.05) !important; }

                .user-avatar-placeholder {
                    width: 45px;
                    height: 45px;
                    background: var(--secondary-color);
                    color: black;
                    border-radius: 40% 60% 70% 30% / 40% 50% 60% 70%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 1.2rem;
                }

                .info-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 0.8rem;
                    color: rgba(255,255,255,0.7);
                }

                .item-icon { color: var(--secondary-color); font-size: 0.9rem; }

                .status-indicator {
                    display: inline-flex;
                    align-items: center;
                    padding: 6px 15px;
                    border-radius: 2px;
                    font-size: 0.65rem;
                    font-weight: 700;
                    letter-spacing: 2px;
                }

                .meta-badge {
                    display: inline-block;
                    padding: 6px 15px;
                    background: rgba(255,255,255,0.05);
                    color: rgba(255,255,255,0.6);
                    font-size: 0.65rem;
                    letter-spacing: 2px;
                    margin-right: 15px;
                    border-radius: 2px;
                }

                .btn-status-update {
                    background: transparent;
                    border: 1px solid;
                    color: #fff;
                    padding: 12px;
                    border-radius: 2px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    letter-spacing: 2px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border-color: rgba(255,255,255,0.15);
                }

                .btn-status-update:hover { background: rgba(255,255,255,0.05); border-color: var(--secondary-color); color: var(--secondary-color); }

                .btn-delete-booking {
                    background: rgba(220, 53, 69, 0.1);
                    border: 1px solid rgba(220, 53, 69, 0.2);
                    color: #dc3545;
                    padding: 12px;
                    border-radius: 2px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    letter-spacing: 2px;
                    transition: all 0.3s ease;
                }

                .btn-delete-booking:hover { background: #dc3545; color: white; }

                .spinner { animation: spin 2s linear infinite; }

                /* Confirmation Modal Specifics */
                .confirm-modal {
                    max-width: 400px;
                }

                .confirm-icon-wrap {
                    width: 70px;
                    height: 70px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                    font-size: 2rem;
                }

                .btn-admin-outline {
                    background: transparent;
                    border: 1px solid rgba(255,255,255,0.2);
                    color: white;
                    padding: 12px 24px;
                    font-size: 0.75rem;
                    letter-spacing: 2px;
                    transition: all 0.3s ease;
                }

                .btn-admin-outline:hover {
                    border-color: #fff;
                    background: rgba(255,255,255,0.05);
                }

                .admin-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.85);
                    backdrop-filter: blur(10px);
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }

                .admin-modal-content {
                    width: 100%;
                    background: #0a0a0a;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 4px;
                    overflow: hidden;
                    box-shadow: 0 50px 100px rgba(0,0,0,0.5);
                }

                .modal-header-custom {
                    padding: 25px 30px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header-custom h4 {
                    font-size: 0.75rem;
                    letter-spacing: 3px;
                    font-weight: 400;
                }

                .btn-close-modal {
                    background: none;
                    border: none;
                    color: rgba(255,255,255,0.4);
                    font-size: 1.2rem;
                    transition: color 0.3s ease;
                }

                .btn-close-modal:hover { color: #fff; }

                .modal-body-custom {
                    padding: 40px;
                }

                .btn-admin-submit {
                    background: var(--secondary-color);
                    border: none;
                    color: black;
                    padding: 12px 24px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    letter-spacing: 2px;
                    border-radius: 2px;
                    transition: all 0.3s ease;
                }

                .btn-admin-submit:hover:not(:disabled) {
                    background: #d4ae6b;
                    transform: translateY(-3px);
                }

                .btn-admin-submit:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .delete-glow {
                    background: rgba(220, 53, 69, 0.1);
                    color: #dc3545;
                    border: 1px solid rgba(220, 53, 69, 0.3);
                    box-shadow: 0 0 30px rgba(220, 53, 69, 0.1);
                }

                .btn-delete-final {
                    background: #dc3545 !important;
                    color: white !important;
                    border: none !important;
                    box-shadow: 0 5px 15px rgba(220, 53, 69, 0.2);
                }

                .btn-delete-final:hover:not(:disabled) {
                    background: #bb2d3b !important;
                    transform: translateY(-3px);
                }
            `}</style>
        </div>
    );
};

export default AdminBookings;
